import { useEffect, useMemo, useRef, useState } from 'react';

import { Trans } from '@lingui/react/macro';
import { Button } from '@repo/videa-components/src/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/videa-components/src/ui/select';
import { format, formatDistanceToNow } from 'date-fns';
import { useAtomValue } from 'jotai';
import { useParams } from 'react-router';

import type { LatestImage } from '@/@types/latestImages';
import { selectedVisitAtom } from '@/app/appHeader/imageViewerMenu/atoms';
import { parseAndFormatDate } from '@/utils/parseDate';
import { PracticeFeatureFlagKey, useFeatureFlag } from '@/utils/user';
import type { ValidUniversalToothId } from '@videa/utils/src/normalizeToothIds';

import { useGetDateFNSLocale } from '../../../../locales/helpers';
import { useLatestImagesV2ToV1 } from '../../../../react-query/useLatestImagesv2';
import { usePatientVisits } from '../../../../react-query/usePatientVisits';
import { mixpanelEvents, useTrackEvent } from '../../../../utils/mixpanel';
import { useMapImageTypeToLabel } from '../../../../utils/string';
import {
  findImageWithMaxMatchingToothNumbers,
  hasOutdatedRblAnalysis,
} from '../../../imageHistory/imageHistorySection/utils';
import { useRotation } from '../../multiFileViewer/RotationContext';
import { CompareImage } from '../image/CompareImage';

import { CompareImageCarousel } from './CompareImageCarousel';
import { ComparisonModeToggle } from './ComparisonModeToggle';
import { NoHistoricalXrayPlaceholder } from './NoHistoricalXrayPlaceholder';

// Minimum delay between automatic image selections to prevent race conditions
const AUTO_SELECTION_DEBOUNCE_MS = 250;

// Helper function to compute image type counts for a given set of images
const computeImageTypeCounts = (images: LatestImage[]) => {
  const counts: { [key: string]: number } = {};

  images.forEach((image) => {
    const imageType = image.detectedImageType;
    if (imageType) {
      counts[imageType] = (counts[imageType] || 0) + 1;
    }
  });

  return Object.entries(counts).map(([type, count]) => ({
    type: type as
      | 'BITEWING'
      | 'PERIAPICAL'
      | 'PANORAMIC'
      | 'OTHER'
      | 'COLOR_IMAGE',
    count,
  }));
};

// Helper function to sort images - this is the single source of truth for image ordering
// Used by both the auto-selection logic and passed to the carousel for consistent display
const sortImagesByPriority = (images: LatestImage[]): LatestImage[] => {
  return images.sort((a, b) => {
    // Helper function to get sort priority
    const getSortPriority = (image: LatestImage): number => {
      // Priority 1: Images with fmxIndex (highest priority)
      if (image.fmxIndex !== null && image.fmxIndex !== undefined) {
        return 1;
      }

      // Priority 2: X-rays with no fmxIndex (BITEWING and PERIAPICAL)
      if (
        (image.detectedImageType === 'BITEWING' ||
          image.detectedImageType === 'PERIAPICAL') &&
        !image.fmxIndex
      ) {
        return 2;
      }

      // Priority 3: COLOR_IMAGE
      if (image.detectedImageType === 'COLOR_IMAGE') {
        return 3;
      }

      // Default priority for any other images
      return 4;
    };

    const priorityA = getSortPriority(a);
    const priorityB = getSortPriority(b);

    // If priorities are different, sort by priority (lower number = higher priority)
    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }

    // If same priority, maintain stable sort by using fmxIndex for FMX images
    if (priorityA === 1 && priorityB === 1) {
      const fmxA = a.fmxIndex || 0;
      const fmxB = b.fmxIndex || 0;
      return fmxA - fmxB;
    }

    // For other cases, maintain original order
    return 0;
  });
};

interface CompareSectionProps {
  section: 'left' | 'right';
  rightSectionImage?: LatestImage | null;
  onImageChange?: (image: LatestImage | null) => void;
  onVisitChange?: (visitId: number) => void;
  selectedTeeth?: ValidUniversalToothId[];
}

export const CompareSection = ({
  section,
  rightSectionImage,
  onImageChange,
  onVisitChange,
  selectedTeeth = [],
}: CompareSectionProps) => {
  const { imageId } = useParams<{ imageId?: string }>();
  const [selectedImageId, setSelectedImageId] = useState<string | undefined>(
    undefined,
  );
  const [selectedVisit, setSelectedVisit] = useState<number>(-1);
  const [isPhotoMode, setIsPhotoMode] = useState<boolean>(false);
  // Track the debounce timeout to prevent race conditions
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Track if we're currently debouncing to show loading state
  const [isDebouncing, setIsDebouncing] = useState<boolean>(false);

  const { id: patientId } = useParams<{ id: string }>();
  const dateLocale = useGetDateFNSLocale();
  const { trackEvent } = useTrackEvent();
  const isSalesDemo = !!useFeatureFlag(PracticeFeatureFlagKey.IS_SALES_DEMO)
    ?.value;
  const mapImageTypeToLabel = useMapImageTypeToLabel();

  // Get the selected visit from the atom used in the image viewer header
  const viewerSelectedVisit = useAtomValue(selectedVisitAtom);

  const {
    saveAndRerunVisit,
    currentlyRotatingImages,
    rotatedImages,
    isLoadingRerun,
  } = useRotation();

  const { data: patientVisitsData, isLoading: isLoadingVisits } =
    usePatientVisits({
      patientId,
    });

  const visits = useMemo(
    () => patientVisitsData?.visits || [],
    [patientVisitsData?.visits],
  );
  const currentVisit = useMemo(
    () => visits.find((visit) => visit.id === selectedVisit),
    [visits, selectedVisit],
  );

  const patientVideaIdForImages = currentVisit?.patientVideaId || patientId;

  const { data: latestImagesForVisit, isLoading: isLoadingImages } =
    useLatestImagesV2ToV1({
      videaPatientId: patientVideaIdForImages,
      estimatedVisitId: selectedVisit ? String(selectedVisit) : undefined,
    });

  // Check for outdated analysis
  const hasOutdatedRbl = hasOutdatedRblAnalysis(
    latestImagesForVisit?.analyzedImages,
  );

  // Filter images based on selected teeth
  const filteredImages = useMemo(() => {
    if (!latestImagesForVisit?.analyzedImages || selectedTeeth.length === 0) {
      return latestImagesForVisit?.analyzedImages || [];
    }

    return latestImagesForVisit.analyzedImages.filter((image) => {
      const imageToothNumbers =
        image.models?.tooth_identification?.findings.map(
          (finding) => finding.toothNumber,
        ) || [];

      // Check if any of the selected teeth are present in this image
      return selectedTeeth.some((selectedTooth) =>
        imageToothNumbers.includes(selectedTooth),
      );
    });
  }, [latestImagesForVisit?.analyzedImages, selectedTeeth]);

  // Filter images based on photo mode
  const photoModeFilteredImages = useMemo(() => {
    if (!isPhotoMode) {
      return filteredImages;
    }

    // When in photo mode, only show COLOR_IMAGE type images
    return filteredImages.filter(
      (image) => image.detectedImageType === 'COLOR_IMAGE',
    );
  }, [filteredImages, isPhotoMode]);

  // Create sorted version of filtered images for consistent carousel display
  const sortedFilteredImages = useMemo(() => {
    return sortImagesByPriority([...photoModeFilteredImages]);
  }, [photoModeFilteredImages]);

  // Auto image selection logic
  useEffect(() => {
    if (!sortedFilteredImages.length) {
      return;
    }

    // Don't auto-select images if we should show the placeholder (no historical visits and no manual selection)
    if (
      section === 'left' &&
      visits.length === 1 &&
      !selectedImageId &&
      !isPhotoMode
    ) {
      return;
    }

    // Clear any existing debounce timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }

    // Set loading state while debouncing
    setIsDebouncing(true);

    // Set a new debounce timeout to prevent race conditions
    // This ensures that rapid changes to dependencies (like rightSectionImage)
    // don't cause immediate image switching, giving the previous image time to load
    debounceTimeoutRef.current = setTimeout(() => {
      // Use the pre-sorted images for consistent selection
      const availableImages = sortedFilteredImages;

      // If the current imageId exists in the available images, use it
      const currentImageExists = availableImages.find(
        (img) => img.imageId === imageId,
      );
      if (currentImageExists) {
        setSelectedImageId(imageId);
        setIsDebouncing(false);
        return;
      }

      // For left section, try to find the best matching image based on tooth numbers from right section image
      if (section === 'left' && rightSectionImage) {
        const idealToothArray =
          rightSectionImage.models?.tooth_identification?.findings.map(
            (finding) => finding.toothNumber,
          ) || [];

        // First, try to find an image with the same type as the right section image
        if (rightSectionImage.detectedImageType) {
          const sameTypeImages = availableImages.filter(
            (img) =>
              img.detectedImageType === rightSectionImage.detectedImageType,
          );

          if (sameTypeImages.length > 0) {
            // Among same-type images, find the one with the most matching tooth numbers
            if (idealToothArray.length > 0) {
              const matchingImageId = findImageWithMaxMatchingToothNumbers(
                idealToothArray,
                sameTypeImages,
              );

              const bestMatchingImage = sameTypeImages.find(
                (img) => img.imageId === matchingImageId,
              );

              if (bestMatchingImage?.imageId) {
                setSelectedImageId(bestMatchingImage.imageId);
                setIsDebouncing(false);
                return;
              }
            }
          }
        }

        // Fallback to the original logic: find the best matching image based on tooth numbers
        if (idealToothArray.length > 0) {
          const matchingImageId = findImageWithMaxMatchingToothNumbers(
            idealToothArray,
            availableImages,
          );

          const bestMatchingImage = availableImages.find(
            (img) => img.imageId === matchingImageId,
          );

          if (bestMatchingImage?.imageId) {
            setSelectedImageId(bestMatchingImage.imageId);
            setIsDebouncing(false);
            return;
          }
        }
      }

      // Default fallback: use the first available image from the sorted array
      if (availableImages.length > 0) {
        setSelectedImageId(availableImages[0].imageId);
      }
      setIsDebouncing(false);
    }, AUTO_SELECTION_DEBOUNCE_MS);

    // Cleanup function to clear the timeout on component unmount
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = null;
      }
      setIsDebouncing(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    imageId,
    sortedFilteredImages,
    section,
    rightSectionImage,
    isPhotoMode,
    visits.length,
  ]);

  // Sets the default selected visit upon receiving new or initial patient visits
  useEffect(() => {
    if (!visits.length) {
      return;
    }

    // If in photo mode, the visit selection will be handled by the toggle component
    if (isPhotoMode) {
      return;
    }

    // For right section - always use the most recent visit (first in the list)
    if (section === 'right') {
      setSelectedVisit(visits[0].id || -1);
      return;
    }

    // For left section
    if (section === 'left') {
      if (viewerSelectedVisit) {
        const viewerSelectedVisitNumber = Number(viewerSelectedVisit);
        const viewerVisitIndex = visits.findIndex(
          (visit) => visit.id === viewerSelectedVisitNumber,
        );

        // If the viewer selected visit is the most recent (index 0), use the next visit
        if (viewerVisitIndex === 0 && visits.length > 1) {
          setSelectedVisit(visits[1].id || -1);
          return;
        }

        // Otherwise use the viewer selected visit
        if (viewerVisitIndex !== -1) {
          setSelectedVisit(viewerSelectedVisitNumber);
          return;
        }
      }
    }
    // If we can't find a valid visit, set to -1
    setSelectedVisit(-1);
  }, [section, visits, isPhotoMode, viewerSelectedVisit]);

  const selectedImage = sortedFilteredImages.find(
    (image) => image.imageId === selectedImageId,
  );

  // Notify parent component when section image changes
  useEffect(() => {
    if (onImageChange) {
      onImageChange(selectedImage || null);
    }
  }, [selectedImage, onImageChange]);

  // Notify parent component when visit changes
  useEffect(() => {
    if (onVisitChange && selectedVisit !== -1) {
      onVisitChange(selectedVisit);
    }
  }, [selectedVisit, onVisitChange]);

  const computeSelectedVisitString = (id: number) => {
    const visitIndex = visits.findIndex((v) => v.id === id);
    if (visitIndex === -1 || !visits[visitIndex]?.visitDate) {
      return '';
    }

    const date = new Date(visits[visitIndex].visitDate);

    return `${parseAndFormatDate(date.toISOString(), 'PP', dateLocale)}`;
  };

  // Function to get images for a specific visit and compute image type counts
  const getVisitImageTypeCounts = (visitId: number) => {
    const visit = visits.find((v) => v.id === visitId);
    if (!visit) return [];

    // If the visit has imageTypeCounts from the API, use those
    if (visit.imageTypeCounts && visit.imageTypeCounts.length > 0) {
      return visit.imageTypeCounts;
    }

    // Otherwise, we need to compute from the current images if this is the selected visit
    if (visitId === selectedVisit && latestImagesForVisit?.analyzedImages) {
      return computeImageTypeCounts(latestImagesForVisit.analyzedImages);
    }

    return [];
  };

  const handleImageSelect = (newImageId: string) => {
    setSelectedImageId(newImageId);
    // Reset the debounce timer for manual selections
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
    setIsDebouncing(false);

    // Track image selection event
    trackEvent(mixpanelEvents.compareImageSelect, {
      section,
      visitId: selectedVisit,
      imageId: newImageId,
      imageType: sortedFilteredImages.find((img) => img.imageId === newImageId)
        ?.detectedImageType,
    });
  };

  const handleVisitChange = (value: string) => {
    const newVisitId = Number(value);
    const previousVisitId = selectedVisit;
    setSelectedVisit(newVisitId);

    // Notify parent component of visit change
    if (onVisitChange) {
      onVisitChange(newVisitId);
    }

    trackEvent(mixpanelEvents.compareVisitChange, {
      section,
      fromVisitId: previousVisitId,
      toVisitId: newVisitId,
      patientId,
      totalVisitsAvailable: visits.length,
      visitId: newVisitId,
    });
  };

  // Rerun logic
  const hasNoImages =
    !sortedFilteredImages.length && !isLoadingImages && !isLoadingVisits;

  const shouldShowRerun = section === 'left' && hasOutdatedRbl && !hasNoImages;

  const shouldShowRotationRerun =
    selectedImageId &&
    currentlyRotatingImages?.imageIds.includes(selectedImageId);

  const allImagesForReanalysis = latestImagesForVisit?.analyzedImages?.map(
    (image) => ({
      imageId: image.imageId,
      rotation: 360,
    }),
  );

  const handleTriggerRerun = () => {
    if (
      (!allImagesForReanalysis && !shouldShowRotationRerun) ||
      selectedVisit === -1 ||
      !selectedImageId
    ) {
      return;
    }

    // Track rerun event
    trackEvent(mixpanelEvents.rerunAnalysis, {
      section,
      visitId: selectedVisit,
      imageId: selectedImageId,
      rerunType: shouldShowRerun ? 'all_images' : 'single_image',
      hasOutdatedRbl,
    });

    // If we are rerunning all images pass through all images
    if (allImagesForReanalysis && shouldShowRerun) {
      saveAndRerunVisit(allImagesForReanalysis, selectedVisit);
      return;
    }

    // Otherwise just pass the currently selected image
    saveAndRerunVisit(
      [
        {
          imageId: selectedImageId,
          rotation: rotatedImages[selectedImageId] * -1,
        },
      ],
      selectedVisit,
    );
  };

  // Check if we should show the no historical x-ray placeholder
  const shouldShowNoHistoricalPlaceholder =
    section === 'left' &&
    visits.length === 1 &&
    !isLoadingVisits &&
    !isLoadingImages &&
    !isPhotoMode &&
    !selectedImageId;

  return (
    <div
      className="vai-w-full vai-max-w-full vai-h-full vai-flex vai-flex-col vai-gap-4 vai-overflow-hidden"
      data-testid={`compare-section-${section}`}
    >
      <div className="vai-flex vai-items-center vai-gap-4 vai-flex-shrink-0">
        <Select
          value={selectedVisit.toString()}
          onValueChange={handleVisitChange}
        >
          <SelectTrigger
            data-testId="compare-section-visit-select"
            className="!vai-bg-[#3F3E43] !vai-w-fit vai-border-0 vai-text-gray-200 vai-rounded-md vai-px-3 vai-py-2 focus:vai-ring-0 focus:!vai-ring-offset-0 focus:vai-outline-none"
          >
            <SelectValue placeholder="Select a visit">
              {selectedVisit !== -1
                ? computeSelectedVisitString(selectedVisit)
                : 'Select a visit'}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {visits.map(
              (visit) =>
                visit.id && (
                  <SelectItem
                    key={visit.id}
                    value={visit.id.toString()}
                    className="vai-flex vai-items-center vai-gap-3 vai-px-3 vai-py-2"
                  >
                    <span className="vai-text-sm vai-font-medium">
                      {computeSelectedVisitString(visit.id)}
                    </span>
                    {getVisitImageTypeCounts(visit.id).map((typeCount) => {
                      const { type, count } = typeCount;
                      if (type === 'OTHER') return null;
                      const countNumber =
                        typeof count === 'number' ? count : Number(count);
                      return (
                        <span
                          key={`${mapImageTypeToLabel[type]}-${countNumber}`}
                          className="vai-inline-block vai-px-2 vai-py-1 vai-bg-gray-600 vai-rounded vai-text-white vai-text-xs vai-font-normal vai-ml-1"
                        >
                          {mapImageTypeToLabel[type]}
                          <span className="vai-font-bold vai-ml-1">
                            {countNumber}
                          </span>
                        </span>
                      );
                    })}
                  </SelectItem>
                ),
            )}
          </SelectContent>
        </Select>

        <ComparisonModeToggle
          section={section}
          isPhotoMode={isPhotoMode}
          visits={visits}
          selectedVisit={selectedVisit}
          onToggleMode={setIsPhotoMode}
          onVisitChange={setSelectedVisit}
        />

        {/* Rerun Button */}
        {(shouldShowRerun || shouldShowRotationRerun) && (
          <Button
            onClick={handleTriggerRerun}
            variant="default"
            size="sm"
            disabled={isLoadingRerun}
            className="vai-bg-sapphire-600 hover:vai-bg-sapphire-700 vai-text-white vai-font-medium vai-px-4 vai-py-2 vai-rounded-md vai-transition-colors"
          >
            {isLoadingRerun ? (
              <div className="vai-flex vai-items-center vai-gap-2">
                <div className="vai-animate-spin vai-rounded-full vai-h-4 vai-w-4 vai-border-t-2 vai-border-b-2 vai-border-white"></div>
                <Trans>Rerunning...</Trans>
              </div>
            ) : (
              <Trans>Rerun AI</Trans>
            )}
          </Button>
        )}

        {currentVisit?.visitDate && (
          <span className="vai-text-gray-400">
            {isSalesDemo && section === 'right'
              ? '10 minutes ago'
              : formatDistanceToNow(new Date(currentVisit.visitDate), {
                  addSuffix: true,
                  locale: dateLocale,
                })}
          </span>
        )}
      </div>

      <div className="vai-w-full vai-max-w-full vai-flex-1 vai-relative vai-min-h-0 vai-overflow-hidden vai-max-h-[calc(100vh-300px)]">
        {shouldShowNoHistoricalPlaceholder && !isPhotoMode ? (
          <NoHistoricalXrayPlaceholder />
        ) : isLoadingVisits || isLoadingImages || isDebouncing ? (
          <div className="vai-absolute vai-inset-0 vai-flex vai-items-center vai-justify-center">
            <div className="vai-animate-spin vai-rounded-full vai-h-8 vai-w-8 vai-border-2 vai-border-t-transparent vai-border-sapphire-600"></div>
          </div>
        ) : !selectedImage ? (
          <div className="vai-absolute vai-inset-0 vai-flex vai-items-center vai-justify-center vai-text-gray-500">
            {selectedTeeth.length > 0 ? (
              <Trans>No images found with selected teeth</Trans>
            ) : (
              <Trans>No images found for this visit</Trans>
            )}
          </div>
        ) : (
          <div
            className="vai-w-full vai-h-full vai-max-h-full"
            data-testid="compare-section-image"
          >
            <CompareImage
              activeImage={selectedImage}
              section={section}
              visitId={selectedVisit.toString()}
            />
          </div>
        )}
      </div>
      <div className="vai-w-full vai-max-w-full vai-flex-shrink-0 vai-overflow-hidden">
        <CompareImageCarousel
          visitId={selectedVisit}
          onImageSelect={handleImageSelect}
          selectedImageId={selectedImageId}
          section={section}
          filteredImages={sortedFilteredImages}
        />
      </div>
    </div>
  );
};
